import { format } from "date-fns";
import { Box } from "@mui/material";
import { es } from "date-fns/locale";
import { useFormik } from "formik";
import { useSnackbar } from "notistack";
import { Fragment, useEffect, useState } from "react";
import { useQueryClient } from "react-query";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import {
    cancelRequest,
    linkingDocumentsToRequestInBackOffice,
    linkingDocumentsToRequestInSoftExpert,
    uploadFormDocuments,
} from "../../../../api/RequestService";
import siritLogo from "../../../../assets/images/siritLogo.png";
import transferenciaLogo from "../../../../assets/images/transferenciaLogo.png";
import FormModal from "../../../../components/FormModal/FormModal";
import ImportantInformationModal from "../../../../components/ImportantInformationModal/ImportantInformationModal";
import { SweetAlert } from "../../../../components/SweetAlert/SweetAlert";
import UploadFile from "../../../../components/UploadFile/UploadFile";
import { HideGlobalLoading, ShowGlobalLoading } from "../../../../redux/actions/UiActions";
import { SmallHeightDivider, StyledButton } from "../../../../theme/Styles";
import COLORS from "../../../../theme/Colors";
import FONTS_FAMILY from "../../../../theme/FontsFamily";
import {
    ButtonContainer,
    Container,
    PaymentMethodsGrid,
    ImageContainer,
    ImageContainerTitle,
    LogoImage,
} from "../../styles/ServiceRequestedDetailsStyles";
import { FileFormSchema } from "./PaymentsConstants";

const sectionCardStyle = {
    background: '#ffffff',
    borderRadius: '14px',
    border: '1px solid #e2e8f0',
    padding: '24px',
    marginBottom: '20px',
    boxShadow: 'none',
};

const sectionTitleStyle = {
    margin: '0 0 20px 0',
    fontSize: '17px',
    fontWeight: 700,
    color: COLORS.secondary,
    fontFamily: FONTS_FAMILY.bold.body,
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
};

const detailItemStyle = {
    padding: '14px 18px',
    background: '#f8fafc',
    borderRadius: '10px',
    border: '1px solid #f1f5f9',
    minHeight: '60px',
};

const detailLabelStyle = {
    margin: 0,
    fontSize: '11px',
    fontWeight: 600,
    color: '#94a3b8',
    fontFamily: FONTS_FAMILY.bold.body,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    marginBottom: '4px',
};

const detailValueStyle = {
    margin: 0,
    fontSize: '15px',
    fontWeight: 500,
    color: COLORS.secondary,
    fontFamily: FONTS_FAMILY.regular.body,
    wordBreak: 'break-word',
};

function Payment() {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    let { requestID } = useParams();
    const queryClient = useQueryClient();
    const requestData = queryClient.getQueryData(["serviceRequestedDetail", requestID]);
    const userData = queryClient.getQueryData(["userData"]);

    const [showSubmitModal, setShowSubmitModal] = useState(false);
    const [modalPaymentIsOpen, setModalPaymentIsOpen] = useState();
    const [paymentAmount, setPaymentAmount] = useState();

    const [isSending, setIsSending] = useState(false);

    const fileFormik = useFormik({
        initialValues: {
            file: {
                files: [],
            },
        },
        validationSchema: FileFormSchema,
        onSubmit: (values) => {
            uploadVoucher(values);
        },
    });

    const handlePaymentModalVisibility = () => {
        setModalPaymentIsOpen(!modalPaymentIsOpen);
    };

    const handleShowSubmitModal = () => {
        setShowSubmitModal(!showSubmitModal);
    };

    const handleModalSubmit = () => {
        fileFormik.handleSubmit();
        handleShowSubmitModal();
    };

    const handleSiritePayment = () => {
        const requiredActionData = requestData?.requiredActions.find((action) => action.action_id === 5 || action.action_id === 7);

        const siritePaymentConfig = {
            codigoCentroRecaudacion: requestData.request.service.institution.recaudationCode,
            codigoServicio: requestData.request.service.sirit_code,
            montoServicio: paymentAmount,
            nombre: `${userData.payload.name} ${userData.payload.first_last_name} ${userData.payload.second_last_name}`,
            numeroDocumento: userData.payload.citizen_id,
            tipoDocumento: "C",
            medioPago: "PagoEnLinea",
            idAutorizacionPortal: requestData.request.idAutorizacionPortal,
            urlRetorno: process.env.REACT_APP_SIRITE_RETURN_URL + requestData.request.id + `?requiredActionID=${requiredActionData?.id}`,
        };

        let form = document.createElement("form");
        form.style.display = "none";
        form.action = process.env.REACT_APP_SIRITE_ENVIROMENT;
        form.method = "POST";
        let siriteConfigKeys = Object.keys(siritePaymentConfig);
        siriteConfigKeys.forEach((key, index) => {
            const input = document.createElement("input");
            input.name = key;
            input.value = siritePaymentConfig[key];
            form.appendChild(input);
        });
        document.body.appendChild(form);
        form.submit();
        document.body.removeChild(form);
    };

    const uploadVoucher = async (values) => {
        let data = values.file.files;
        if (data.length === 0) {
            return;
        }
        dispatch(ShowGlobalLoading(`Iniciando`));
        setIsSending(true);
        const formFilesData = new FormData();
        formFilesData.append("file[]", data[0], data[0].name);
        try {
            const uploadFilesConfig = {
                onUploadProgress: (progressEvent) => {
                    var percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    dispatch(ShowGlobalLoading(`Subiendo documentos ${percentCompleted}%`));
                },
            };
            const requiredActionData = requestData?.requiredActions.find((action) => action.action_id === 5 || action.action_id === 7);
            let responseFilesUploadResponse = await uploadFormDocuments(formFilesData, uploadFilesConfig);
            if (responseFilesUploadResponse?.success) {
                const backOfficeRequest = {
                    required_action_id: requiredActionData?.id,
                    voucher: true,
                    status: true,
                    documents: responseFilesUploadResponse.files.map((file, index) => {
                        return {
                            ...file,
                            label: "Comprobante de pago",
                        };
                    }),
                };
                dispatch(ShowGlobalLoading(`Por favor espere mientras se procesa la solicitud...`));
                let linkBackOfficeResponse = await linkingDocumentsToRequestInBackOffice(backOfficeRequest, requestID);
                if (linkBackOfficeResponse.success) {
                    //GOOD THE VOUCHER IS SEND SUCCESSFULL TO BACKOFFICE
                    const activityId = requestData?.request?.activity?.activity_id
                        ? requestData?.request?.activity?.activity_id
                        : requestData?.request?.service?.activity_id;
                    dispatch(ShowGlobalLoading(`Finalizando el proceso...`));
                    let softExpertResponse = await linkingDocumentsToRequestInSoftExpert(
                        linkBackOfficeResponse?.request_code,
                        [
                            {
                                ...responseFilesUploadResponse.files[0],
                                label: "Comprobante de pago",
                            },
                        ],
                        { activity_id: activityId, advance_activity: true }
                    );
                    if (softExpertResponse.success) {
                        enqueueSnackbar("Comprobante de pago enviado", { variant: "success" });
                        queryClient.invalidateQueries(["serviceRequestedDetail", requestID]);
                    } else {
                        enqueueSnackbar("Ha ocurrido un error", { variant: "error" });
                    }
                } else {
                    enqueueSnackbar("Ha ocurrido un error", { variant: "error" });
                }

                setModalPaymentIsOpen(false);
                dispatch(HideGlobalLoading());
            } else {
                enqueueSnackbar("Ha ocurrido un error", { variant: "error" });
            }
        } catch (error) {
            enqueueSnackbar("Ha ocurrido un error", { variant: "error" });
            setIsSending(false);
            setModalPaymentIsOpen(false);
            dispatch(HideGlobalLoading());
        }
    };

    useEffect(() => {
        if (requestData?.request !== undefined) {
            //Payment by required action
            if (requestData.request.request_actions_id === 7) {
                //put the payment_amount in requestData.priceRequest
                setPaymentAmount(requestData.request.payment.payment_amount);
                return;
            }
            //Initial Payment
            if (requestData.request.payment.payment_status === "PENDIENTE") {
                setPaymentAmount(requestData.request.payment.payment_amount);
                return;
            }

            if (requestData.request.payment.payment_status === "GENERADO") {
                setPaymentAmount(requestData.request.payment.payment_amount);
                return;
            }
        }
    }, [requestData]);

    useEffect(() => {
        const unloadCallback = (event) => {
            //cancelRequest(requestData?.request?.id);
            event.preventDefault();
            event.returnValue = "Si cierra esta pestaña el proceso de la solicitud se vera afectado";
            return "Si cierra esta pestaña el proceso de la solicitud se vera afectado";
        };

        const alertUser = (event) => {
            SweetAlert.fire("Advertencia", "Si cierra esta pestaña el proceso de la solicitud se vera afectado", "warning");
            event.preventDefault();
            event.returnValue = "Si cierra esta pestaña el proceso de la solicitud se vera afectado";
            return "Si cierra esta pestaña el proceso de la solicitud se vera afectado";
        };
        if (isSending) {
            window.addEventListener("beforeunload", alertUser);
            window.addEventListener("unload", unloadCallback);
            return () => {
                window.removeEventListener("beforeunload", alertUser);
                window.removeEventListener("unload", unloadCallback);
            };
        }
    }, [isSending]);

    return (
        <Container>
            {requestData.request.payment.payment_status === "PENDIENTE" && (
                <div style={sectionCardStyle}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                        <h3 style={sectionTitleStyle}>
                            <span style={{ fontSize: '20px' }}>💳</span>
                            Método de pago
                        </h3>
                        {paymentAmount && (
                            <span style={{ fontFamily: FONTS_FAMILY.bold.title, fontSize: '17px', fontWeight: 700, color: COLORS.secondary }}>
                                Monto a pagar DOP${paymentAmount}
                            </span>
                        )}
                    </div>

                    <PaymentMethodsGrid>
                        {requestData.request.service.sirit_code != null && requestData?.request?.status?.base_status_id == 1 && (
                            <ImageContainer title="Sirite" onClick={() => handleSiritePayment()}>
                                <LogoImage src={siritLogo} />
                                <ImageContainerTitle>PAGO CON TARJETA</ImageContainerTitle>
                            </ImageContainer>
                        )}
                        {requestData.request.service.external_pay !== 0 && requestData?.request?.status?.base_status_id == 1 && (
                            <ImageContainer title="Transferecia" onClick={handlePaymentModalVisibility}>
                                <LogoImage src={transferenciaLogo} />
                                <ImageContainerTitle>DEPÓSITO O TRANSFERENCIA</ImageContainerTitle>
                            </ImageContainer>
                        )}
                    </PaymentMethodsGrid>
                </div>
            )}

            {requestData.request.payment.payment_status === "GENERADO" && (
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    background: '#eef2ff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '14px',
                    padding: '18px 24px',
                    marginBottom: '20px',
                }}>
                    <span style={{ fontSize: '20px' }}>⏳</span>
                    <span style={{ fontFamily: FONTS_FAMILY.bold.body, fontSize: '14.5px', fontWeight: 700, color: COLORS.secondary }}>
                        Su pago de ${paymentAmount} está en proceso de validación.
                    </span>
                </div>
            )}

            <ImportantInformationModal
                open={showSubmitModal}
                onBackDropClick={handleShowSubmitModal}
                onCloseClick={handleShowSubmitModal}
                CloseTitle="Cancelar"
                CloseButton
                buttonTitle="Confirmar"
                buttonClick={handleModalSubmit}
                content={
                    <Fragment>
                        <p>Favor verificar que el documento suministrado es correcto.</p>
                        <strong>
                            Una vez enviado el comprobante esta acción no se podra revertir, en caso de cancelar durante el envío de dicho
                            documento la solicitud será desestimada.
                        </strong>
                    </Fragment>
                }
            />

            <FormModal 
                onClose={handlePaymentModalVisibility} 
                open={modalPaymentIsOpen} 
                title="Subir comprobante"
                maxWidth="sm"
                fullWidth={true}
            >
                <SmallHeightDivider />
                <SmallHeightDivider />
                <SmallHeightDivider />
                <UploadFile
                    id="file"
                    title="Documento"
                    maxFileSize={25}
                    onChange={fileFormik.handleChange}
                    onBlur={fileFormik.handleBlur}
                    value={fileFormik.values.file}
                    error={fileFormik.touched.file && Boolean(fileFormik.errors.file)}
                    helperText={fileFormik.touched.file && fileFormik.errors.file}
                    required
                />

                <SmallHeightDivider />
                <SmallHeightDivider />

                <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', mt: 3, mb: 1 }}>
                    <StyledButton 
                        onClick={handleShowSubmitModal} 
                        disabled={isSending}
                        sx={{
                            width: { xs: '100%', sm: '240px' },
                            mx: 'auto'
                        }}
                    >
                        CONFIRMAR
                    </StyledButton>
                </Box>
            </FormModal>

            {/*requestData.request.payment.payment_status === "PAGADO" &&
                <Fragment>
                    <TextInformation title="Detalles de pago" />
                    <SmallHeightDivider />
                    <SmallHeightDivider />
                    <Grid alignItems="center" justifyContent="flex-start" container direction="row" spacing={{ xs: 2, md: 3 }} columns={{ xs: 6, sm: 8, md: 12 }}>

                        <Grid item xs={6} sm={4} md={4}>
                            <BodyTextBold>
                                Numero de aprobacion:
                            </BodyTextBold>
                            <BodyText>
                                {requestData.request.approval_number}
                            </BodyText>
                        </Grid>

                        <Grid item xs={6} sm={4} md={4}>
                            <BodyTextBold>
                                Monto pagado:
                            </BodyTextBold>
                            <BodyText>
                                {requestData.request.payment.payment_amount}
                            </BodyText>
                        </Grid>

                        <Grid item xs={6} sm={4} md={4}>
                            <BodyTextBold>
                                Fecha de pago:
                            </BodyTextBold>
                            <BodyText>
                                {format(new Date(requestData.request.payment.succesfullyPayment_date.replace(" ", "T")), "dd 'de' MMMM yyyy", { locale: es })}
                            </BodyText>
                        </Grid>

                    </Grid>
                </Fragment>
        */}

            {requestData.priceRequest.length > 0 && (
                <div style={sectionCardStyle}>
                    <h3 style={sectionTitleStyle}>
                        <span style={{ fontSize: '20px' }}>🧾</span>
                        Mis pagos
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        {requestData.priceRequest.map((payment) => (
                            <div key={payment.id} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px' }}>
                                <div style={detailItemStyle}>
                                    <p style={detailLabelStyle}>Pago</p>
                                    <p style={detailValueStyle}>{payment.concept}</p>
                                </div>

                                <div style={detailItemStyle}>
                                    <p style={detailLabelStyle}>ID de pago</p>
                                    <p style={detailValueStyle}>{payment.approval_number}</p>
                                </div>

                                <div style={detailItemStyle}>
                                    <p style={detailLabelStyle}>Método de pago</p>
                                    <p style={detailValueStyle}>{payment.payment_method}</p>
                                </div>

                                <div style={detailItemStyle}>
                                    <p style={detailLabelStyle}>Monto pagado</p>
                                    <p style={detailValueStyle}>{payment.price}</p>
                                </div>

                                <div style={detailItemStyle}>
                                    <p style={detailLabelStyle}>Moneda</p>
                                    <p style={detailValueStyle}>{payment.coin}</p>
                                </div>

                                <div style={detailItemStyle}>
                                    <p style={detailLabelStyle}>Fecha de pago</p>
                                    <p style={detailValueStyle}>
                                        {format(new Date(payment.created_at.replace(" ", "T")), "dd 'de' MMMM yyyy", {
                                            locale: es,
                                        })}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </Container>
    );
}

export default Payment;
